import React, { useState, useEffect, useCallback } from 'react';
import { X, Link as LinkIcon, Loader2, Check } from 'lucide-react';
import { SUPPORTED_CURRENCIES, getCurrencySymbol } from '../services/currencyService';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../contexts/AuthContext';
import { fetchProductInfo } from '../services/scraperService';
import { fetchProductInfoFallback } from '../services/scraperServiceFallback';
import { scrapeInBrowser } from '../services/scraperServiceFrontend';
import CategorySelector from './CategorySelector';

const WishForm = ({ wish, onSubmit, onCancel, defaultCurrency }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  // Determine the currency to use: list currency > user preference > NOK
  const getDefaultCurrency = useCallback(() => {
    return defaultCurrency || user?.preferences?.currency || 'NOK';
  }, [defaultCurrency, user?.preferences?.currency]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    price: '',
    currency: getDefaultCurrency(),
    priority: 'medium',
    category: '',
    imageUrl: ''
  });

  const [isScraping, setIsScraping] = useState(false);
  const [scrapingMessage, setScrapingMessage] = useState('');
  const [scrapedData, setScrapedData] = useState(null);
  const [scrapedImageUrl, setScrapedImageUrl] = useState(null);

  useEffect(() => {
    if (wish) {
      setFormData({
        name: wish.name || '',
        description: wish.description || '',
        url: wish.url || '',
        price: wish.price || '',
        currency: wish.currency || getDefaultCurrency(),
        priority: wish.priority || 'medium',
        category: wish.category || '',
        imageUrl: wish.imageUrl || ''
      });
      if (wish.imageUrl) {
        setScrapedImageUrl(wish.imageUrl);
      }
    }
  }, [wish, getDefaultCurrency]);
  
  // Separate effect to update currency when default changes (only for new wishes)
  useEffect(() => {
    if (!wish) {
      setFormData(prev => ({
        ...prev,
        currency: getDefaultCurrency()
      }));
    }
  }, [getDefaultCurrency, wish]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleUrlBlur = async (e) => {
    const url = e.target.value.trim();
    
    if (!url) return;
    
    // Basic URL validation
    try {
      new URL(url);
    } catch {
      return; // Invalid URL, ignore silently
    }

    setIsScraping(true);
    setScrapedData(null);
    setScrapingMessage('');
    
    // Check if we should skip backend scraping (sites that block scraping)
    const isKomplett = url.includes('komplett.no');
    const isPowerNo = url.includes('power.no');
    
    try {
      let productData;
      
      // Skip slow backend scraping for sites that block iframe access anyway
      if (isKomplett || isPowerNo) {
        console.log('Skipping backend scraping for blocked site, using fallback');
        productData = null; // Will trigger fallback
      } else {
        productData = await fetchProductInfo(url);
      }
      
      if (productData) {
        console.log('Scraped product data:', productData);
        setScrapedData(productData);
        
        // Check if data is incomplete and needs frontend scraping
        const needsFrontendScraping = !productData.price || !productData.image;
        const isPowerNo = url.includes('power.no');
        const isKomplett = url.includes('komplett.no');
        
        // Some sites block iframe access due to CORS/X-Frame-Options, so skip frontend scraping
        if (needsFrontendScraping && !isPowerNo && !isKomplett) {
          // Show loading message
          setScrapingMessage(t('wishForm.extractingMissingData'));
          
          try {
            const frontendData = await scrapeInBrowser(url);
            
            // Merge frontend data with backend data
            if (frontendData && (frontendData.price || frontendData.image)) {
              const mergedData = {
                ...productData,
                price: productData.price || frontendData.price,
                image: productData.image || frontendData.image
              };
              
              console.log('Merged product data (backend + frontend):', mergedData);
              setScrapedData(mergedData);
              
              // Update form with merged data
              setFormData(prev => ({
                ...prev,
                name: prev.name || mergedData.name || '',
                price: prev.price || (mergedData.price ? String(mergedData.price) : ''),
                description: prev.description || mergedData.description || '',
                imageUrl: mergedData.image || '',
                category: prev.category || mergedData.suggestedCategory || ''
              }));
              
              if (mergedData.image) {
                setScrapedImageUrl(mergedData.image);
              }
            } else {
              // Fallback to original productData if frontend scraping failed
              setFormData(prev => ({
                ...prev,
                name: prev.name || productData.name || '',
                price: prev.price || (productData.price ? String(productData.price) : ''),
                description: prev.description || productData.description || '',
                imageUrl: productData.image || '',
                category: prev.category || productData.suggestedCategory || ''
              }));
            }
          } catch (frontendError) {
            console.log('Frontend scraping failed, using partial data:', frontendError);
            // Gracefully continue with partial data from backend
            setFormData(prev => ({
              ...prev,
              name: prev.name || productData.name || '',
              price: prev.price || (productData.price ? String(productData.price) : ''),
              description: prev.description || productData.description || '',
              imageUrl: productData.image || '',
              category: prev.category || productData.suggestedCategory || ''
            }));
          }
        } else {
          // Backend scraping was complete, or Power.no (which blocks iframe access)
          setFormData(prev => {
            const newData = {
              ...prev,
              name: prev.name || productData.name || '',
              price: prev.price || (productData.price ? String(productData.price) : ''),
              description: prev.description || productData.description || '',
              imageUrl: productData.image || '',
              category: prev.category || productData.suggestedCategory || ''
            };
            console.log('Updated form data:', newData);
            return newData;
          });
        }
        
        if (productData.image) {
          setScrapedImageUrl(productData.image);
        }
      } else {
        // No product data from backend, use fallback directly
        console.log('No product data from backend, using fallback scraper');
        const fallbackData = await fetchProductInfoFallback(url); // Use fallback directly
        if (fallbackData) {
          setScrapedData(fallbackData);
          setFormData(prev => ({
            ...prev,
            name: prev.name || fallbackData.name || '',
            price: prev.price || (fallbackData.price ? String(fallbackData.price) : ''),
            description: prev.description || fallbackData.description || '',
            imageUrl: fallbackData.image || '',
            category: prev.category || fallbackData.suggestedCategory || ''
          }));
          if (fallbackData.image) {
            setScrapedImageUrl(fallbackData.image);
          }
        }
      }
    } catch (error) {
      console.error('Error scraping product:', error);
      // Silent failure - no error message to user
    } finally {
      setIsScraping(false);
      setScrapingMessage('');
    }
  };

  const handleCategorySelect = (categoryPath) => {
    setFormData(prev => ({
      ...prev,
      category: categoryPath
    }));
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      return;
    }

    // Helper function to normalize URL
    const normalizeUrl = (url) => {
      if (!url.trim()) return '';
      
      const trimmedUrl = url.trim();
      
      // If URL already has a protocol, return as is
      if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
        return trimmedUrl;
      }
      
      // If URL starts with www. or looks like a domain, add https://
      if (trimmedUrl.startsWith('www.') || 
          (trimmedUrl.includes('.') && !trimmedUrl.includes(' '))) {
        return `https://${trimmedUrl}`;
      }
      
      // For other cases, return as is (might be a relative path or invalid)
      return trimmedUrl;
    };

    const submitData = {
      ...formData,
      name: formData.name.trim(),
      description: formData.description.trim(),
      url: normalizeUrl(formData.url),
      price: formData.price ? parseFloat(formData.price) : null,
      priority: formData.priority,
      category: formData.category || null,
      imageUrl: formData.imageUrl || scrapedImageUrl || ''
    };

    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-2 sm:mx-4 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            {wish ? t('wishForm.editWish') : t('wishForm.addWish')}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
          {/* URL Field - First Priority */}
          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
              {t('wishForm.productLink')}
            </label>
            <div className="space-y-2">
              <div className="flex space-x-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {isScraping ? (
                      <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                    ) : scrapedData ? (
                      <Check className="h-5 w-5 text-green-500" />
                    ) : (
                      <LinkIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <input
                    type="url"
                    id="url"
                    name="url"
                    className="input-field pl-10"
                    placeholder="https://amazon.com/product or https://komplett.no/product"
                    value={formData.url}
                    onChange={(e) => {
                      handleChange(e);
                      // Auto-scrape when URL is pasted/typed
                      if (e.target.value && e.target.value.length > 10) {
                        handleUrlBlur(e);
                      }
                    }}
                  />
                </div>
              </div>
              
              <p className="text-sm text-gray-500">
                {t('wishForm.productLinkHelp')}
              </p>
              
              {/* Scraping Message */}
              {isScraping && scrapingMessage && (
                <p className="text-xs text-blue-600 mt-1">
                  {scrapingMessage}
                </p>
              )}
            </div>
          </div>

          {/* Product Preview Card */}
          {scrapedData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start space-x-3">
                {scrapedData.image && (
                  <img 
                    src={scrapedData.image} 
                    alt="Product" 
                    className="w-12 h-12 object-cover rounded"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-900 truncate">
                    {scrapedData.name}
                  </p>
                  {scrapedData.price && (
                    <p className="text-sm text-blue-700">
                      {scrapedData.price}
                    </p>
                  )}
                  {scrapedData.suggestedCategory && (
                    <p className="text-xs text-blue-600 mt-1">
                      📂 Auto-detected: {scrapedData.suggestedCategory}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Item Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('wishForm.itemName')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              className="input-field"
              placeholder={t('wishForm.itemNamePlaceholder')}
              value={formData.name}
              onChange={handleChange}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              {t('wishForm.description')}
            </label>
            <textarea
              id="description"
              name="description"
              rows={2}
              className="input-field"
              placeholder={t('wishForm.descriptionPlaceholder')}
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          {/* Category Selector */}
          <CategorySelector
            selectedCategory={formData.category}
            onSelect={handleCategorySelect}
          />

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                {t('wishForm.price')}
              </label>
              <div className="flex space-x-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-400 text-sm">
                      {getCurrencySymbol(formData.currency)}
                    </span>
                  </div>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    step="0.01"
                    min="0"
                    className="input-field pl-8 text-lg"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={handleChange}
                  />
                </div>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="input-field w-32"
                >
                  {SUPPORTED_CURRENCIES.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                {t('wishForm.priority')}
              </label>
              <select
                id="priority"
                name="priority"
                className="input-field"
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="low">🌱 {t('wishForm.priorityLow')}</option>
                <option value="medium">⭐ {t('wishForm.priorityMedium')}</option>
                <option value="high">🔥 {t('wishForm.priorityHigh')}</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {t('wishForm.priorityHelp')}
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              {wish ? t('wishForm.updateWish') : t('wishForm.addWish')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WishForm;
